import '@fontsource/roboto'
import '@fontsource/poppins'
import { Suspense } from 'react'
import { Outlet } from 'react-router-dom'
import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { defineStyle, defineStyleConfig } from '@chakra-ui/styled-system'
import { AuthProvider } from '@/shared/context/AuthContext'
import { ReactQueryProvider } from '@/shared/lib/react-query-provider'
import PageLoader from '@/shared/components/PageLoader'

const fonts = {
    body: "'Roboto', sans-serif",
    heading: "'Poppins', san-serif"
}

// The color from figma is '#28275E' (brand.400); the rest is tinted & saturated using HSB
const colors = {
    brand: {
        50: '#CFCFFF',
        100: '#bbb9e7',
        200: '#6D6AFF',
        300: '#5451C4',
        400: '#28275E',
        500: '#28275E',
        600: '#3b3989',
        700: '#323176',
        800: '#181738',
        900: '#242355'
    }
}

const buttonBaseStyle = defineStyle({
    fontWeight: 'normal',
    fontFamily: 'Roboto'
})

const buttonTheme = defineStyleConfig({
    baseStyle: buttonBaseStyle,
    defaultProps: {
        colorScheme: 'brand'
    }
})

const styles = {
    global: {
        a: {
            color: '#5451C4'
        }
    }
}

const theme = extendTheme({
    fonts,
    colors,
    styles,
    components: { Button: buttonTheme }
})

// Outer providers — wrap RouterProvider in main.tsx. No Router-dependent hooks may run here.
export function Providers({ children }: { children: React.ReactNode }) {
    return (
        <ReactQueryProvider>
            <ChakraProvider theme={theme}>{children}</ChakraProvider>
        </ReactQueryProvider>
    )
}

// Router-aware providers — used as the root route's element so AuthProvider can call useNavigate.
export function AuthLayout() {
    return (
        <AuthProvider>
            <Suspense fallback={<PageLoader />}>
                <Outlet />
            </Suspense>
        </AuthProvider>
    )
}
